import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { vcomsatSyncApi, VehiclePosition, trafficSignApi, TrafficSign } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { VcomsatCarDetail } from '../types';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const formatLocalTime = (utcTimeString: string | Date): string => {
  if (!utcTimeString) return '';
  let date: Date;
  if (utcTimeString instanceof Date) {
    date = utcTimeString;
  } else {
    const timeStr = utcTimeString.toString();
    date = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z');
  }
  return date.toLocaleString();
};

const formatLocalTimeOnly = (utcTimeString: string | Date): string => {
  if (!utcTimeString) return '';
  let date: Date;
  if (utcTimeString instanceof Date) {
    date = utcTimeString;
  } else {
    const timeStr = utcTimeString.toString();
    date = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z');
  }
  return date.toLocaleTimeString();
};

const HOME_LOCATION = {
  latitude: 10.27218,
  longitude: 106.35878,
  name: 'Phu Tan, Vinh Long'
};

const ROUTE_COLOR = '#1a5c1a';

const getRouteColor = (_vcomsatCarDetailId: number): string => ROUTE_COLOR;

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const isDrivingAwayFromHome = (lat: number, lon: number): boolean => {
  const distanceFromHome = calculateDistance(HOME_LOCATION.latitude, HOME_LOCATION.longitude, lat, lon);
  return distanceFromHome > 1;
};

const createIcon = (color: 'green' | 'red' | 'blue') => {
  const fillColor = color === 'green' ? '#28a745' : color === 'red' ? '#dc3545' : '#007bff';
  const svgIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32"><path fill="' + fillColor + '" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white"/></svg>';
  return L.divIcon({
    html: svgIcon,
  className: 'custom-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Traffic sign icons
const createTrafficSignIcon = (signType: string, speedLimit?: number) => {
  let svgIcon = '';
  
  if (signType === 'speedLimit' && speedLimit) {
    // Speed limit sign - red circle with white center and speed number
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="28" height="28">
    <circle cx="16" cy="16" r="14" fill="#dc3545" stroke="#fff" stroke-width="2"/>
      <circle cx="16" cy="16" r="10" fill="white"/>
      <text x="16" y="20" text-anchor="middle" font-size="11" font-weight="bold" fill="#333">${speedLimit}</text>
    </svg>`;
  } else if (signType === 'speedCamera') {
    // Speed camera - camera icon
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
      <rect x="4" y="8" width="24" height="16" rx="2" fill="#ffc107" stroke="#333" stroke-width="1"/>
      <circle cx="16" cy="16" r="5" fill="#333"/>
      <circle cx="16" cy="16" r="3" fill="#666"/>
  <rect x="22" y="10" width="4" height="3" fill="#333"/>
    </svg>`;
} else if (signType === 'camera') {
    // General camera
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="22" height="22">
      <rect x="4" y="8" width="24" height="16" rx="2" fill="#6c757d" stroke="#333" stroke-width="1"/>
<circle cx="16" cy="16" r="5" fill="#333"/>
      <circle cx="16" cy="16" r="3" fill="#888"/>
    </svg>`;
  } else if (signType === 'dangerArea') {
 // Danger - yellow triangle
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
  <path d="M16 4 L30 28 L2 28 Z" fill="#ffc107" stroke="#333" stroke-width="1"/>
      <text x="16" y="24" text-anchor="middle" font-size="16" font-weight="bold" fill="#333">!</text>
    </svg>`;
  } else if (signType === 'accidentArea') {
    // Accident area - warning
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
   <path d="M16 4 L30 28 L2 28 Z" fill="#fd7e14" stroke="#333" stroke-width="1"/>
      <text x="16" y="24" text-anchor="middle" font-size="14" font-weight="bold" fill="#fff">⚠</text>
    </svg>`;
  } else if (signType === 'constructionArea') {
    // Construction - orange diamond
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
    <rect x="6" y="6" width="20" height="20" transform="rotate(45 16 16)" fill="#fd7e14" stroke="#333" stroke-width="1"/>
      <text x="16" y="20" text-anchor="middle" font-size="12" font-weight="bold" fill="#333">🚧</text>
    </svg>`;
  } else if (signType === 'policeArea') {
    // Police - blue shield
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
      <path d="M16 2 L28 8 L28 18 C28 24 16 30 16 30 C16 30 4 24 4 18 L4 8 Z" fill="#0d6efd" stroke="#fff" stroke-width="1"/>
      <text x="16" y="20" text-anchor="middle" font-size="12" font-weight="bold" fill="#fff">P</text>
    </svg>`;
  } else if (signType === 'speedLimitEnd') {
    // End of speed limit - gray with diagonal line
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
   <circle cx="16" cy="16" r="14" fill="#e9ecef" stroke="#6c757d" stroke-width="2"/>
    <line x1="6" y1="26" x2="26" y2="6" stroke="#6c757d" stroke-width="3"/>
    </svg>`;
  } else if (signType === 'startUrban') {
 // Start of urban zone - green rectangle with house icon (entering residential area)
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
 <rect x="2" y="6" width="28" height="20" rx="2" fill="#28a745" stroke="#fff" stroke-width="1"/>
      <path d="M16 10 L24 16 L24 24 L8 24 L8 16 Z" fill="white"/>
      <rect x="13" y="18" width="6" height="6" fill="#28a745"/>
    </svg>`;
  } else if (signType === 'endUrban') {
// End of urban zone - gray rectangle with house and diagonal line (leaving residential area)
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
      <rect x="2" y="6" width="28" height="20" rx="2" fill="#6c757d" stroke="#fff" stroke-width="1"/>
      <path d="M16 10 L24 16 L24 24 L8 24 L8 16 Z" fill="white" opacity="0.7"/>
   <rect x="13" y="18" width="6" height="6" fill="#6c757d"/>
      <line x1="4" y1="24" x2="28" y2="8" stroke="#dc3545" stroke-width="3"/>
    </svg>`;
  } else if (signType === 'roadMarker') {
    // Road marker - small blue circle
  svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="18" height="18">
      <circle cx="16" cy="16" r="8" fill="#0d6efd" stroke="#fff" stroke-width="2"/>
    </svg>`;
  } else if (signType === 'trafficLight') {
    // Traffic light - vertical rectangle with 3 colored circles
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="24" height="24">
      <rect x="10" y="2" width="12" height="28" rx="2" fill="#333" stroke="#222" stroke-width="1"/>
      <circle cx="16" cy="8" r="4" fill="#dc3545"/>
      <circle cx="16" cy="16" r="4" fill="#ffc107"/>
<circle cx="16" cy="24" r="4" fill="#28a745"/>
    </svg>`;
  } else {
    // Default - info marker
    svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="20" height="20">
  <circle cx="16" cy="16" r="12" fill="#17a2b8" stroke="#fff" stroke-width="2"/>
      <text x="16" y="21" text-anchor="middle" font-size="14" font-weight="bold" fill="#fff">i</text>
    </svg>`;
  }
  
  // Determine icon size based on sign type
  let iconSize: [number, number] = [24, 24];
  let iconAnchor: [number, number] = [12, 12];
  
  if (signType === 'speedLimit') {
    iconSize = [28, 28];
    iconAnchor = [14, 14];
  } else if (signType === 'roadMarker') {
    iconSize = [18, 18];
    iconAnchor = [9, 9];
  }
  
  return L.divIcon({
    html: svgIcon,
className: 'traffic-sign-icon',
    iconSize: iconSize,
    iconAnchor: iconAnchor,
    popupAnchor: [0, -12],
  });
};

// Get friendly name for sign type
const getSignTypeName = (signType: string): string => {
  const names: Record<string, string> = {
    'speedLimit': 'Giới hạn tốc độ',
    'speedLimitEnd': 'Hết giới hạn tốc độ',
    'speedCamera': 'Camera bắn tốc độ',
    'camera': 'Camera giám sát',
    'dangerArea': 'Khu vực nguy hiểm',
    'accidentArea': 'Khu vực hay xảy ra tai nạn',
    'constructionArea': 'Khu vực thi công',
    'policeArea': 'Khu vực CSGT',
    'residential': 'Khu dân cư',
    'startUrban': 'Bắt đầu khu dân cư',
    'endUrban': 'Hết khu dân cư',
    'roadMarker': 'Mốc đường',
    'trafficLight': 'Đèn giao thông',
  };
  return names[signType] || signType;
};

const greenIcon = createIcon('green');
const redIcon = createIcon('red');
const homeIcon = createIcon('blue');

interface VehicleTrackingMapProps {
  isWidget?: boolean;
  selectedVehicleId?: number;
}

interface VehicleRoute {
  vcomsatCarDetailId: number;
  coordinates: [number, number][];
  color: string;
}

const MapController: React.FC<{
  positions: VehiclePosition[];
  selectedPosition: VehiclePosition | null;
  routes: VehicleRoute[];
  homeLocation: { latitude: number; longitude: number };
}> = ({ positions, selectedPosition, routes, homeLocation }) => {
  const map = useMap();
  const initialFitDone = useRef(false);

  useEffect(() => {
    if (selectedPosition && (selectedPosition.latitude !== 0 || selectedPosition.longitude !== 0)) {
      map.setView([selectedPosition.latitude, selectedPosition.longitude], 16);
    } else if (!initialFitDone.current && (positions.length > 0 || routes.length > 0)) {
      const allPoints: [number, number][] = [];
      allPoints.push([homeLocation.latitude, homeLocation.longitude]);
      positions.filter(p => p.latitude !== 0 || p.longitude !== 0).forEach(p => allPoints.push([p.latitude, p.longitude]));
      routes.forEach(route => route.coordinates.forEach(coord => allPoints.push(coord)));
      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        initialFitDone.current = true;
      }
    }
  }, [map, positions, selectedPosition, routes, homeLocation]);

  useEffect(() => {
    if (routes.length === 0) initialFitDone.current = false;
  }, [routes.length]);

  return null;
};

const VehicleTrackingMap: React.FC<VehicleTrackingMapProps> = ({ isWidget = false, selectedVehicleId }) => {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState<VcomsatCarDetail[]>([]);
  const [vehiclePositions, setVehiclePositions] = useState<VehiclePosition[]>([]);
  const [vehicleRoutes, setVehicleRoutes] = useState<VehicleRoute[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<number | 'all'>(selectedVehicleId || 'all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focusedPosition, setFocusedPosition] = useState<VehiclePosition | null>(null);
  const [vehicleListExpanded, setVehicleListExpanded] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  
  // Traffic signs state
  const [trafficSigns, setTrafficSigns] = useState<TrafficSign[]>([]);
  const [showTrafficSigns, setShowTrafficSigns] = useState(false); // Changed to false by default
  const [trafficSignsAvailable, setTrafficSignsAvailable] = useState(false);
  const [trafficSignsLoading, setTrafficSignsLoading] = useState(false);
  const [trafficSignsLoaded, setTrafficSignsLoaded] = useState(false); // Track if signs have been loaded
  
  // Urban boundary signs state
  const [urbanBoundarySigns, setUrbanBoundarySigns] = useState<TrafficSign[]>([]);
  const [showUrbanBoundaries, setShowUrbanBoundaries] = useState(false);
const [urbanBoundariesLoading, setUrbanBoundariesLoading] = useState(false);
  const [urbanBoundariesLoaded, setUrbanBoundariesLoaded] = useState(false);

  const defaultCenter: [number, number] = [10.8231, 106.6297];

  // Sort vehicles by status: moving first, then stopped
  const sortVehiclesByStatus = (positions: VehiclePosition[]): VehiclePosition[] => {
    return [...positions].sort((a, b) => {
      const aIsMoving = a.status !== 1 && a.speed > 5;
      const bIsMoving = b.status !== 1 && b.speed > 5;
      
      // Moving vehicles come first (return -1 if a is moving and b is not)
      if (aIsMoving && !bIsMoving) return -1;
      if (!aIsMoving && bIsMoving) return 1;
      
      // If both have same status, maintain original order
      return 0;
    });
  };

  // Get sorted vehicle positions for display
  const sortedVehiclePositions = sortVehiclesByStatus(vehiclePositions);

  // Check traffic sign service availability
  useEffect(() => {
    const checkTrafficSignService = async () => {
      try {
        console.log('[TrafficSigns] Checking service availability...');
    const response = await trafficSignApi.getStatus();
console.log('[TrafficSigns] Service status:', response.data);
        setTrafficSignsAvailable(response.data.isAvailable);
      } catch (err) {
  console.warn('[TrafficSigns] Service not available:', err);
  setTrafficSignsAvailable(false);
      }
    };
    checkTrafficSignService();
  }, []);

  const loadVehicles = useCallback(async () => {
    try {
    const response = await vcomsatSyncApi.getSyncedVehicles();
      setVehicles(response.data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    setError(t('failed.to.load.vehicles'));
    }
  }, [t]);

  const loadVehicleRoute = useCallback(async (vcomsatCarDetailId: number, carPlate: string) => {
    try {
      // Add timeout wrapper for the API call
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000) // 10 second timeout
      );
   
      const apiPromise = vcomsatSyncApi.getCurrentJourney(
        vcomsatCarDetailId,
      HOME_LOCATION.latitude,
        HOME_LOCATION.longitude,
        0.5
      );
      
    const response = await Promise.race([apiPromise, timeoutPromise]) as any;
      const journeyData = response.data;
      
      if (journeyData.hasJourney && journeyData.routePoints && journeyData.routePoints.length > 0) {
        const coordinates: [number, number][] = [
[HOME_LOCATION.latitude, HOME_LOCATION.longitude],
       ...journeyData.routePoints.map((p: any) => [p.latitude, p.longitude] as [number, number])
      ];
        return { vcomsatCarDetailId, coordinates, color: getRouteColor(vcomsatCarDetailId) };
      }
    } catch (err: any) {
    if (err.message === 'Request timeout') {
      console.warn('Timeout loading journey for vehicle ' + carPlate);
      } else {
        console.error('Failed to load journey for vehicle ' + vcomsatCarDetailId + ':', err.message);
      }
    }
    return null;
  }, []);

  // Load traffic signs along all routes
  const loadTrafficSigns = useCallback(async (routes: VehicleRoute[]) => {
    console.log('[TrafficSigns] loadTrafficSigns called, available:', trafficSignsAvailable, 'routes:', routes.length);
    
    if (!trafficSignsAvailable || routes.length === 0) {
      console.log('[TrafficSigns] Skipping - not available or no routes');
      setTrafficSigns([]);
   return;
    }

setTrafficSignsLoading(true);
    try {
   // Combine all route points from all routes
      const allRoutePoints: { lat: number; lng: number }[] = [];
      routes.forEach(route => {
        route.coordinates.forEach(([lat, lng]) => {
          allRoutePoints.push({ lat, lng });
        });
      });

   console.log('[TrafficSigns] Total route points:', allRoutePoints.length);

      if (allRoutePoints.length < 2) {
        setTrafficSigns([]);
 return;
      }

      // Sample route points if too many (for performance)
      let sampledPoints = allRoutePoints;
      if (allRoutePoints.length > 1000) {
 const sampleInterval = Math.ceil(allRoutePoints.length / 1000);
     sampledPoints = allRoutePoints.filter((_, i) => i % sampleInterval === 0);
        // Always include first and last
    if (!sampledPoints.includes(allRoutePoints[allRoutePoints.length - 1])) {
          sampledPoints.push(allRoutePoints[allRoutePoints.length - 1]);
        }
    }

      console.log('[TrafficSigns] Calling API with', sampledPoints.length, 'points');
      const response = await trafficSignApi.getSignsAlongRoute(sampledPoints, 100);
      console.log('[TrafficSigns] API response:', response.data.length, 'signs found');
      setTrafficSigns(response.data);
      setTrafficSignsLoaded(true); // Mark as loaded
    } catch (err) {
      console.error('[TrafficSigns] Failed to load traffic signs:', err);
      setTrafficSigns([]);
    } finally {
      setTrafficSignsLoading(false);
    }
  }, [trafficSignsAvailable]);

  // Load urban boundary signs based on current map view
  const loadUrbanBoundarySigns = useCallback(async (bounds?: L.LatLngBounds) => {
    console.log('[UrbanBoundaries] loadUrbanBoundarySigns called, available:', trafficSignsAvailable);
    
    if (!trafficSignsAvailable) {
   console.log('[UrbanBoundaries] Skipping - service not available');
      return;
    }

    // Get bounds from map or use default around home location
    let bbox: { minLat: number; maxLat: number; minLng: number; maxLng: number };
    
    if (bounds) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
      bbox = {
 minLat: sw.lat,
        maxLat: ne.lat,
   minLng: sw.lng,
        maxLng: ne.lng
      };
      
      // Check if bounds are too large (max ~50km x 50km = 0.5 degrees)
      if ((bbox.maxLat - bbox.minLat) > 0.5 || (bbox.maxLng - bbox.minLng) > 0.5) {
        console.log('[UrbanBoundaries] Map view too large, limiting to 0.5 degree box');
     // Center the box on the current view center
        const centerLat = (bbox.minLat + bbox.maxLat) / 2;
        const centerLng = (bbox.minLng + bbox.maxLng) / 2;
    bbox = {
          minLat: centerLat - 0.25,
          maxLat: centerLat + 0.25,
          minLng: centerLng - 0.25,
          maxLng: centerLng + 0.25
        };
}
    } else {
      // Default: area around home location (~50km)
      bbox = {
        minLat: HOME_LOCATION.latitude - 0.25,
        maxLat: HOME_LOCATION.latitude + 0.25,
        minLng: HOME_LOCATION.longitude - 0.25,
        maxLng: HOME_LOCATION.longitude + 0.25
      };
    }

console.log('[UrbanBoundaries] Loading with bbox:', bbox);
    setUrbanBoundariesLoading(true);
    
    try {
      const response = await trafficSignApi.getUrbanBoundarySigns(bbox);
      console.log('[UrbanBoundaries] API response:', response.data.length, 'signs found');
  setUrbanBoundarySigns(response.data);
      setUrbanBoundariesLoaded(true);
    } catch (err: any) {
    console.error('[UrbanBoundaries] Failed to load urban boundary signs:', err);
      if (err.response?.data?.message) {
        console.error('[UrbanBoundaries] Error message:', err.response.data.message);
      }
      setUrbanBoundarySigns([]);
    } finally {
      setUrbanBoundariesLoading(false);
    }
  }, [trafficSignsAvailable]);

  const loadPositions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await vcomsatSyncApi.getLatestPositions();
      let positions = response.data;
  if (selectedVehicle !== 'all') {
     positions = positions.filter((p: VehiclePosition) => p.vcomsatCarDetailId === selectedVehicle);
    }
      setVehiclePositions(positions);

      // Load routes concurrently with a limit
      const vehiclesAwayFromHome = positions.filter((pos: VehiclePosition) => 
isDrivingAwayFromHome(pos.latitude, pos.longitude)
      );
  
      console.log('[TrafficSigns] Vehicles away from home:', vehiclesAwayFromHome.length);
      
      const routes: VehicleRoute[] = [];
  
      // Process routes concurrently but limit to 3 at a time to avoid overwhelming the server
      const CONCURRENT_LIMIT = 3;
      for (let i = 0; i < vehiclesAwayFromHome.length; i += CONCURRENT_LIMIT) {
        const batch = vehiclesAwayFromHome.slice(i, i + CONCURRENT_LIMIT);
      const batchPromises = batch.map(pos => loadVehicleRoute(pos.vcomsatCarDetailId, pos.carPlate));
        const batchResults = await Promise.allSettled(batchPromises);
 
        batchResults.forEach((result) => {
   if (result.status === 'fulfilled' && result.value) {
            routes.push(result.value);
          }
        });
      }
 
      console.log('[TrafficSigns] Routes loaded:', routes.length);
      setVehicleRoutes(routes);
    
      // Don't load traffic signs automatically - only load when user clicks the button
      // Reset loaded state when routes change so user can reload if needed
      setTrafficSignsLoaded(false);
      setTrafficSigns([]);
      
      setError(null);
    } catch (err) {
      console.error('Failed to load positions:', err);
    setError(t('failed.to.load.positions'));
    } finally {
      setLoading(false);
    }
  }, [selectedVehicle, t, loadVehicleRoute]);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);
  useEffect(() => { loadPositions(); }, [loadPositions]);

  const handleVehicleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  const value = e.target.value;
    setSelectedVehicle(value === 'all' ? 'all' : parseInt(value));
    setFocusedPosition(null);
  };

  const handleRefresh = () => loadPositions();

  const handleVehicleClick = (pos: VehiclePosition) => {
    // Only focus on map if vehicle has valid coordinates
    if (pos.latitude !== 0 || pos.longitude !== 0) {
      setFocusedPosition(pos);
    }
    if (window.innerWidth < 768) setVehicleListExpanded(false);
  };

  // Handle traffic signs button click - only load signs when button is pressed
  const handleTrafficSignsToggle = async () => {
    if (!showTrafficSigns) {
      // Turning on - load signs if not already loaded
      setShowTrafficSigns(true);
if (!trafficSignsLoaded && vehicleRoutes.length > 0) {
        await loadTrafficSigns(vehicleRoutes);
      }
    } else {
      // Turning off - just hide
      setShowTrafficSigns(false);
    }
  };

  // Handle urban boundaries button click
  const handleUrbanBoundariesToggle = async () => {
    if (!showUrbanBoundaries) {
 // Turning on - load signs for current map view
      setShowUrbanBoundaries(true);
      
      // Get current map bounds
      const map = mapRef.current;
   const bounds = map?.getBounds();
      
  await loadUrbanBoundarySigns(bounds || undefined);
    } else {
   // Turning off - just hide
   setShowUrbanBoundaries(false);
      // Clear signs to free memory
   setUrbanBoundarySigns([]);
      setUrbanBoundariesLoaded(false);
    }
  };

  const getMarkerIcon = (status: number, speed: number) => (status === 1 || speed === 0) ? redIcon : greenIcon;
  const toggleVehicleList = () => setVehicleListExpanded(!vehicleListExpanded);

  const renderMap = (height: string, mapId: string) => (
    <MapContainer center={defaultCenter} zoom={isWidget ? 10 : 12} style={{ height, width: '100%' }} ref={mapRef} scrollWheelZoom={true}>
      <TileLayer attribution="OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <MapController positions={vehiclePositions} selectedPosition={focusedPosition} routes={vehicleRoutes} homeLocation={HOME_LOCATION} />
      <Marker position={[HOME_LOCATION.latitude, HOME_LOCATION.longitude]} icon={homeIcon}>
        <Popup>
       <div style={{ minWidth: '150px' }}>
            <h6 style={{ margin: '0 0 8px 0', color: '#007bff' }}>Home - {HOME_LOCATION.name}</h6>
            <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>Nha (Home)</p>
     </div>
        </Popup>
 </Marker>
      {vehicleRoutes.map(route => (
        <Polyline key={'route-' + route.vcomsatCarDetailId} positions={route.coordinates} color={route.color} weight={3} opacity={0.9} />
      ))}
  
      {/* Traffic Signs */}
      {showTrafficSigns && trafficSigns.map((sign, index) => (
        <Marker 
     key={`sign-${index}-${sign.latitude}-${sign.longitude}`} 
  position={[sign.latitude, sign.longitude]} 
          icon={createTrafficSignIcon(sign.signType, sign.speedLimit)}
   >
     <Popup>
     <div style={{ minWidth: '140px' }}>
   <h6 style={{ margin: '0 0 6px 0', color: '#333', fontSize: '13px' }}>
   {getSignTypeName(sign.signType)}
   </h6>
  {sign.speedLimit && (
 <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: 'bold', color: '#dc3545' }}>
  <i className="bi bi-speedometer2 me-1"></i>{sign.speedLimit} km/h
   </p>
   )}
     <p style={{ margin: '2px 0', fontSize: '11px', color: '#666' }}>
  {sign.latitude.toFixed(5)}, {sign.longitude.toFixed(5)}
     </p>
   </div>
 </Popup>
  </Marker>
      ))}

      {/* Urban Boundary Signs (StartUrban and EndUrban) */}
  {showUrbanBoundaries && urbanBoundarySigns.map((sign, index) => (
        <Marker 
          key={`urban-${index}-${sign.latitude}-${sign.longitude}`} 
       position={[sign.latitude, sign.longitude]} 
          icon={createTrafficSignIcon(sign.signType, sign.speedLimit)}
        >
     <Popup>
            <div style={{ minWidth: '140px' }}>
       <h6 style={{ margin: '0 0 6px 0', color: sign.signType === 'startUrban' ? '#28a745' : '#6c757d', fontSize: '13px' }}>
          {getSignTypeName(sign.signType)}
              </h6>
     <p style={{ margin: '2px 0', fontSize: '11px', color: '#666' }}>
           {sign.latitude.toFixed(5)}, {sign.longitude.toFixed(5)}
              </p>
        </div>
          </Popup>
        </Marker>
      ))}
      
 {vehiclePositions.filter(pos => pos.latitude !== 0 || pos.longitude !== 0).map(pos => {
        const statusText = pos.status === 1 || pos.speed === 0 ? t('stopped') : t('moving');
        const showingRoute = vehicleRoutes.find(r => r.vcomsatCarDetailId === pos.vcomsatCarDetailId);
        return (
          <Marker key={pos.vcomsatCarDetailId} position={[pos.latitude, pos.longitude]} icon={getMarkerIcon(pos.status, pos.speed)}>
  <Popup>
       <div style={{ minWidth: '180px' }}>
            <h6 style={{ margin: '0 0 8px 0', color: '#333' }}>{pos.carPlate}</h6>
   <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>{t('status')}:</strong> {statusText}</p>
                <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>{t('speed')}:</strong> {pos.speed} km/h</p>
    <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>{t('address')}:</strong> {pos.address || t('unknown')}</p>
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}><strong>{t('last.update')}:</strong> {formatLocalTime(pos.realTime)}</p>
      {showingRoute && (
          <p style={{ margin: '4px 0', fontSize: '12px', color: showingRoute.color, fontWeight: 'bold' }}>
                 <span style={{ display: 'inline-block', width: '12px', height: '3px', backgroundColor: showingRoute.color, marginRight: '6px', verticalAlign: 'middle' }}></span>
        Route from home
    </p>
            )}
          </div>
            </Popup>
  </Marker>
 );
      })}
    </MapContainer>
  );

  const renderVehicleItem = (pos: VehiclePosition, compact: boolean = false) => {
    const routeInfo = vehicleRoutes.find(r => r.vcomsatCarDetailId === pos.vcomsatCarDetailId);
    const hasPosition = pos.latitude !== 0 || pos.longitude !== 0;
    const isMoving = hasPosition && pos.status !== 1 && pos.speed > 5;
    const isSelected = focusedPosition?.vcomsatCarDetailId === pos.vcomsatCarDetailId;

    return (
      <div
        key={pos.vcomsatCarDetailId}
        onClick={() => handleVehicleClick(pos)}
        style={{
          padding: compact ? '8px 10px' : '12px 14px',
          borderBottom: '1px solid #eee',
          cursor: hasPosition ? 'pointer' : 'default',
        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
          borderLeft: isSelected ? '3px solid #007bff' : '3px solid transparent',
          opacity: hasPosition ? 1 : 0.6
    }}
      >
        <div className="d-flex justify-content-between align-items-center mb-1">
       <div className="d-flex align-items-center">
            <span style={{ width: compact ? '10px' : '12px', height: compact ? '10px' : '12px', borderRadius: '50%', backgroundColor: !hasPosition ? '#999' : isMoving ? '#28a745' : '#dc3545', marginRight: '8px' }}></span>
            <span style={{ fontWeight: '600', fontSize: compact ? '14px' : '15px', color: '#333' }}>
        {routeInfo && <span style={{ display: 'inline-block', width: '14px', height: '4px', backgroundColor: routeInfo.color, marginRight: '8px', verticalAlign: 'middle', borderRadius: '2px' }}></span>}
            {pos.carPlate}
        </span>
          </div>
          <span className={'badge ' + (!hasPosition ? 'bg-secondary' : isMoving ? 'bg-success' : 'bg-danger')} style={{ fontSize: compact ? '10px' : '11px', padding: '3px 8px' }}>
    {!hasPosition ? (t('no.position') || 'No Position') : isMoving ? t('moving') : t('stopped')}
          </span>
     </div>
        {hasPosition && (
        <div className="d-flex justify-content-between align-items-center" style={{ fontSize: compact ? '12px' : '13px' }}>
    <span className="text-muted"><i className="bi bi-speedometer2 me-1"></i>{pos.speed} km/h</span>
 <small className="text-muted" style={{ fontSize: compact ? '11px' : '12px' }}>{formatLocalTime(pos.realTime)}</small>
    </div>
        )}
        {pos.address && !compact && (
 <small className="text-muted d-block mt-1" style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={pos.address}>
            <i className="bi bi-geo-alt me-1"></i>{pos.address}
        </small>
  )}
        {!hasPosition && !compact && (
          <small className="text-muted d-block mt-1" style={{ fontSize: '12px' }}>
            <i className="bi bi-exclamation-circle me-1"></i>{t('no.position.data') || 'No position data - sync journey first'}
          </small>
        )}
      </div>
    );
  };

  if (isWidget) {
    return (
      <div className="card h-100">
        <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ backgroundColor: '#f8f9fa' }}>
          <h6 className="mb-0 d-flex align-items-center">
            <i className="bi bi-geo-alt-fill me-2 text-primary"></i>{t('vehicle.tracking')}
          </h6>
          <div className="d-flex gap-2 align-items-center flex-wrap">
     {/* Always show traffic signs button - disabled if not available */}
            <button 
      className={`btn btn-sm ${showTrafficSigns && trafficSignsAvailable ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={handleTrafficSignsToggle}
              disabled={!trafficSignsAvailable || vehicleRoutes.length === 0}
         title={!trafficSignsAvailable ? t('traffic.signs.not.available') : vehicleRoutes.length === 0 ? t('no.routes.available') : (showTrafficSigns ? t('traffic.signs.hide.full') : t('traffic.signs.show.full'))}
            >
         <i className={`bi bi-signpost-2 ${trafficSignsLoading ? 'spin' : ''}`}></i>
       <span className="d-none d-sm-inline">{t('traffic.signs')}</span>
         {trafficSigns.length > 0 && <span className="ms-1 badge bg-dark">{trafficSigns.length}</span>}
          {!trafficSignsAvailable && <span className="ms-1">?</span>}
            </button>
     {/* Urban boundaries button */}
     <button 
            className={`btn btn-sm ${showUrbanBoundaries && trafficSignsAvailable ? 'btn-success' : 'btn-outline-success'}`}
       onClick={handleUrbanBoundariesToggle}
       disabled={!trafficSignsAvailable}
            title={!trafficSignsAvailable ? 'Dữ liệu không khả dụng' : (showUrbanBoundaries ? 'Ẩn khu dân cư' : 'Hiện khu dân cư')}
    >
         <i className={`bi bi-houses me-1 ${urbanBoundariesLoading ? 'spin' : ''}`}></i>
    <span className="d-none d-sm-inline">Khu dân cư</span>
     {urbanBoundarySigns.length > 0 && <span className="ms-1 badge bg-dark">{urbanBoundarySigns.length}</span>}
          </button>
          <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: '100px', maxWidth: '150px', fontSize: '12px' }} value={selectedVehicle} onChange={handleVehicleChange}>
     <option value="all">{t('all.vehicles')}</option>
     {vehicles.map(v => <option key={v.id} value={v.id}>{v.carPlate}</option>)}
   </select>
         <button className="btn btn-sm btn-outline-primary" onClick={handleRefresh} disabled={loading} title={t('refresh')} style={{ padding: '4px 8px' }}>
      <i className={'bi bi-arrow-clockwise ' + (loading ? 'spin' : '')}></i>
         </button>
        <button className="btn btn-sm btn-outline-secondary d-md-none" onClick={toggleVehicleList} style={{ padding: '4px 8px' }}>
      <i className={'bi ' + (vehicleListExpanded ? 'bi-chevron-up' : 'bi-list')}></i>
            <span className="ms-1">{vehiclePositions.length}</span>
            </button>
    </div>
  </div>
      <div className="card-body p-0">
          {error && <div className="alert alert-warning m-2 mb-0 py-1" style={{ fontSize: '12px' }}><i className="bi bi-exclamation-triangle me-1"></i>{error}</div>}
          {vehicles.length === 0 && !loading && <div className="alert alert-info m-2 mb-0 py-1" style={{ fontSize: '12px' }}><i className="bi bi-info-circle me-1"></i>{t('no.synced.vehicles')}</div>}
  
          <div className={'d-md-none ' + (vehicleListExpanded ? 'd-block' : 'd-none')} style={{ maxHeight: '200px', overflowY: 'auto', borderBottom: '1px solid #dee2e6' }}>
{loading && vehiclePositions.length === 0 ? (
           <div className="d-flex justify-content-center align-items-center p-3"><div className="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            ) : vehiclePositions.length === 0 ? (
              <div className="p-3 text-center text-muted" style={{ fontSize: '12px' }}><i className="bi bi-inbox d-block mb-1" style={{ fontSize: '20px' }}></i>{t('no.position.data')}</div>
            ) : sortedVehiclePositions.map(pos => renderVehicleItem(pos, true))}
          </div>

          <div className="d-none d-md-flex" style={{ height: '320px' }}>
            <div style={{ flex: '1 1 65%', minWidth: 0 }}>
              {loading && vehiclePositions.length === 0 ? (
  <div className="d-flex justify-content-center align-items-center h-100 bg-light">
    <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
    </div>
          ) : renderMap('100%', 'tracking-map-widget')}
       </div>
            <div style={{ flex: '0 0 35%', borderLeft: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', backgroundColor: '#fafbfc' }}>
 <div className="px-2 py-2 d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#f1f3f4', fontSize: '12px', fontWeight: '600' }}>
           <span><i className="bi bi-truck me-1"></i>{t('vehicles')}</span>
      <span className="badge bg-primary" style={{ fontSize: '10px' }}>{vehiclePositions.length}</span>
         </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
       <div className="d-flex justify-content-center align-items-center h-100"><div className="spinner-border spinner-border-sm text-secondary" role="status"></div></div>
             ) : vehiclePositions.length === 0 ? (
     <div className="p-3 text-center text-muted"><i className="bi bi-inbox d-block mb-2" style={{ fontSize: '24px' }}></i>{t('no.position.data')}</div>
    ) : sortedVehiclePositions.map(pos => renderVehicleItem(pos))}
              </div>
     <div className="px-2 py-1" style={{ borderTop: '1px solid #dee2e6', backgroundColor: '#f1f3f4', fontSize: '10px' }}>
          <div className="d-flex justify-content-around flex-wrap">
           <span className="d-flex align-items-center"><span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#28a745', marginRight: '4px' }}></span>{t('moving')}</span>
    <span className="d-flex align-items-center"><span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#dc3545', marginRight: '4px' }}></span>{t('stopped')}</span>
        <span className="d-flex align-items-center"><span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#007bff', marginRight: '4px' }}></span>Home</span>
                  {showTrafficSigns && trafficSigns.length > 0 && (
     <span className="d-flex align-items-center"><i className="bi bi-signpost-2 text-warning" style={{ fontSize: '10px', marginRight: '3px' }}></i>Signs</span>
   )}
       </div>
     </div>
         </div>
          </div>

          <div className="d-md-none" style={{ height: vehicleListExpanded ? '200px' : '320px' }}>
    {loading && vehiclePositions.length === 0 ? (
              <div className="d-flex justify-content-center align-items-center h-100 bg-light">
     <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}></div>
      </div>
 ) : renderMap('100%', 'tracking-map-widget-mobile')}
          </div>
        </div>
      </div>
  );
  }

  return (
    <div className="container-fluid py-3" style={{ height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0">
          <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
          <span className="d-none d-sm-inline">{t('vehicle.tracking')}</span>
        <span className="d-sm-none">{t('tracking') || 'Tracking'}</span>
        </h4>
<div className="d-flex gap-2 flex-wrap">
          {/* Always show traffic signs button - disabled if not available */}
          <button 
     className={`btn btn-sm ${showTrafficSigns && trafficSignsAvailable ? 'btn-warning' : 'btn-outline-warning'}`}
    onClick={handleTrafficSignsToggle}
            disabled={!trafficSignsAvailable || vehicleRoutes.length === 0}
            title={!trafficSignsAvailable ? 'Biển báo không khả dụng (kiểm tra data.wmap.update)' : vehicleRoutes.length === 0 ? 'Không có tuyến đường' : (showTrafficSigns ? 'Ẩn biển báo giao thông' : 'Hiện biển báo giao thông')}
    >
         <i className={`bi bi-signpost-2 me-1 ${trafficSignsLoading ? 'spin' : ''}`}></i>
            <span className="d-none d-sm-inline">Biển báo</span>
            {trafficSigns.length > 0 && <span className="ms-1 badge bg-dark">{trafficSigns.length}</span>}
       {!trafficSignsAvailable && <span className="ms-1">?</span>}
          </button>
          <button 
    className={`btn btn-sm ${showUrbanBoundaries ? 'btn-info' : 'btn-outline-info'}`}
    onClick={handleUrbanBoundariesToggle}
    title={showUrbanBoundaries ? 'Ẩn ranh giới đô thị' : 'Hiện ranh giới đô thị'}
  >
    <i className="bi bi-border-all me-1"></i>
    <span className="d-none d-sm-inline">Ranh giới đô thị</span>
  </button>
          <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: '120px', maxWidth: '180px' }} value={selectedVehicle} onChange={handleVehicleChange}>
            <option value="all">{t('all.vehicles')}</option>
       {vehicles.map(v => <option key={v.id} value={v.id}>{v.carPlate}</option>)}
          </select>
          <button className="btn btn-sm btn-primary" onClick={handleRefresh} disabled={loading}>
        <i className={'bi bi-arrow-clockwise ' + (loading ? 'spin' : '')}></i>
            <span className="d-none d-sm-inline ms-1">{t('refresh')}</span>
      </button>
   <button className="btn btn-sm btn-outline-secondary d-md-none" onClick={toggleVehicleList}>
   <i className={'bi ' + (vehicleListExpanded ? 'bi-x' : 'bi-list-ul')}></i>
         <span className="ms-1">{vehiclePositions.length}</span>
          </button>
        </div>
      </div>

      {error && <div className="alert alert-warning py-2 mb-2"><i className="bi bi-exclamation-triangle me-2"></i>{error}</div>}
      {vehicles.length === 0 && !loading && <div className="alert alert-info py-2 mb-2"><i className="bi bi-info-circle me-2"></i>{t('no.synced.vehicles.hint')}</div>}

      {vehicleListExpanded && (
        <div className="d-md-none mb-2">
          <div className="card">
     <div className="card-header py-2 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
          <h6 className="mb-0"><i className="bi bi-truck me-2"></i>{t('vehicle.list')}</h6>
            <div className="d-flex align-items-center gap-2">
        <span className="badge bg-primary">{vehiclePositions.length}</span>
           <button className="btn btn-sm btn-link p-0" onClick={() => setVehicleListExpanded(false)}><i className="bi bi-x-lg"></i></button>
      </div>
            </div>
            <div className="card-body p-0" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
       {loading ? (
  <div className="d-flex justify-content-center align-items-center p-4"><div className="spinner-border spinner-border-sm text-primary" role="status"></div></div>
   ) : vehiclePositions.length === 0 ? (
         <div className="p-3 text-center text-muted"><i className="bi bi-inbox d-block mb-2" style={{ fontSize: '24px' }}></i>{t('no.position.data')}</div>
              ) : <div className="list-group list-group-flush">{sortedVehiclePositions.map(pos => renderVehicleItem(pos))}</div>}
          </div>
       <div className="card-footer py-2" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="d-flex justify-content-around" style={{ fontSize: '11px' }}>
<span className="d-flex align-items-center"><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#28a745', marginRight: '4px' }}></span>{t('moving')}</span>
     <span className="d-flex align-items-center"><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#dc3545', marginRight: '4px' }}></span>{t('stopped')}</span>
 <span className="d-flex align-items-center"><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#007bff', marginRight: '4px' }}></span>Home</span>
            </div>
            </div>
   </div>
        </div>
      )}

      <div className="row g-3" style={{ flex: 1, minHeight: 0 }}>
        <div className="col-md-9 h-100">
          <div className="card h-100">
            <div className="card-body p-0 h-100">
          {loading && vehiclePositions.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center h-100">
             <div className="spinner-border text-primary" role="status"></div>
   </div>
   ) : renderMap('100%', 'tracking-map-full')}
            </div>
     </div>
     </div>

        <div className="col-md-3 h-100 d-none d-md-block">
   <div className="d-flex flex-column h-100 gap-3">
  <div className="card" style={{ flex: '1 1 65%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    <div className="card-header py-2 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
   <h6 className="mb-0"><i className="bi bi-truck me-2"></i>{t('vehicle.list')}</h6>
                <span className="badge bg-primary">{vehiclePositions.length}</span>
              </div>
  <div className="card-body p-0" style={{ flex: 1, overflowY: 'auto' }}>
  {loading ? (
                  <div className="d-flex justify-content-center align-items-center h-100"><div className="spinner-border spinner-border-sm text-primary" role="status"></div></div>
     ) : vehiclePositions.length === 0 ? (
    <div className="p-3 text-center text-muted"><i className="bi bi-inbox d-block mb-2" style={{ fontSize: '24px' }}></i>{t('no.position.data')}</div>
     ) : <div className="list-group list-group-flush">{sortedVehiclePositions.map(pos => renderVehicleItem(pos))}</div>}
      </div>
            </div>
            <div className="card" style={{ flexShrink: 0 }}>
          <div className="card-header py-2" style={{ backgroundColor: '#f8f9fa' }}>
 <h6 className="mb-0" style={{ fontSize: '13px' }}><i className="bi bi-info-circle me-2"></i>{t('legend')}</h6>
       </div>
          <div className="card-body py-2">
                <div className="d-flex flex-wrap gap-3" style={{ fontSize: '12px' }}>
 <span className="d-flex align-items-center"><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#28a745', marginRight: '6px' }}></span>{t('moving')}</span>
                  <span className="d-flex align-items-center"><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#dc3545', marginRight: '6px' }}></span>{t('stopped')}</span>
    <span className="d-flex align-items-center"><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#007bff', marginRight: '6px' }}></span>Home</span>
                </div>
            <div className="mt-2 pt-2 border-top">
             <small className="text-muted" style={{ fontSize: '11px' }}>
       <span style={{ display: 'inline-block', width: '14px', height: '3px', backgroundColor: '#1a5c1a', marginRight: '6px', verticalAlign: 'middle' }}></span>
        Route from home
       </small>
     </div>
                {/* Traffic signs legend */}
                {showTrafficSigns && trafficSigns.length > 0 && (
  <div className="mt-2 pt-2 border-top">
   <small className="text-muted d-block mb-1" style={{ fontSize: '11px', fontWeight: '600' }}>
     <i className="bi bi-signpost-2 me-1 text-warning"></i>{t('traffic.signs.full')} ({trafficSigns.length})
        </small>
         <div className="d-flex flex-wrap gap-2" style={{ fontSize: '10px' }}>
    {trafficSigns.filter(s => s.signType === 'speedLimit').length > 0 && (
  <span className="badge bg-danger">{t('traffic.signs.legend.speed.limit')}: {trafficSigns.filter(s => s.signType === 'speedLimit').length}</span>
   )}
      {trafficSigns.filter(s => s.signType === 'speedCamera').length > 0 && (
     <span className="badge bg-warning text-dark">{t('traffic.signs.legend.camera')}: {trafficSigns.filter(s => s.signType === 'speedCamera').length}</span>
 )}
   {trafficSigns.filter(s => !['speedLimit', 'speedCamera'].includes(s.signType)).length > 0 && (
       <span className="badge bg-secondary">{t('traffic.signs.legend.other')}: {trafficSigns.filter(s => !['speedLimit', 'speedCamera'].includes(s.signType)).length}</span>
 )}
             </div>
  </div>
   )}
   </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleTrackingMap;
